/**
=========================================================
* Trust Cargo - Payments (money out to suppliers)
=========================================================

* This page is supplier payments only: what we have paid out, to whom and
* when. What customers pay US is a different ledger and lives on the
* Customer Payments screen.
*
* Reading the list wants GET /v2/supplier-payments. Until that is deployed
* the same list is assembled from each supplier's statement, so the page
* works either way and quietly switches to the single request once it can.
*/

import React, { useState, useEffect } from "react";
import axios from "axios";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import CargoLoader from "components/CargoLoader";
import FilterBar, { filterRows } from "components/FilterBar";
import {
  FilterSelect,
  FilterDates,
  optionsFromRows,
  matches,
  inDateRange,
} from "components/FilterBar/controls";
import Pagination, { usePagination } from "components/Pagination";
import IdCell, { IdHead } from "components/IdCell";
import SupplierPaymentDialog from "components/SupplierPaymentDialog";
import RowActions from "components/RowActions";
import SupplierPaymentsDialog from "components/SupplierPaymentsDialog";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";

const API = "https://trust-cargo.co/anas_backend/api/v2";

const money = (v) =>
  v === null || v === undefined || v === ""
    ? "-"
    : Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Every supplier payment.
 *
 * Prefers the one endpoint. The fallback walks the suppliers and reads each
 * statement, which is one request per supplier - correct but not something
 * to rely on once there are a few hundred of them.
 */
async function loadPayments() {
  const direct = await fetch(`${API}/supplier-payments?limit=2000`, { cache: "no-store" }).catch(
    () => null
  );

  if (direct && direct.ok) {
    const rows = await direct.json().catch(() => null);
    if (Array.isArray(rows)) return rows;
  }

  const res = await fetch(`${API}/suppliers`, { cache: "no-store" });
  if (!res.ok) throw new Error(`Suppliers unavailable (HTTP ${res.status})`);

  const suppliers = await res.json();

  const statements = await Promise.all(
    (suppliers || []).map((s) =>
      fetch(`${API}/suppliers/${s.id}/statement`, { cache: "no-store" })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) =>
          (d?.payments || []).map((p) => ({
            ...p,
            supplier_id: s.id,
            supplier: s.name,
            supplier_code: s.code,
          }))
        )
        .catch(() => [])
    )
  );

  return statements
    .flat()
    .sort((a, b) => String(b.paid_at || "").localeCompare(String(a.paid_at || "")));
}

function Payments() {
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(null);
  // the supplier whose full history is open, or null
  const [history, setHistory] = useState(null);
  const [supplier, setSupplier] = useState("");
  const [method, setMethod] = useState("");
  const [busy, setBusy] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await loadPayments());
    } catch (e) {
      setError(e?.message || "Could not load payments");
      setRows([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  /**
   * Delete a payment recorded by mistake.
   *
   * The row stands for money that left the account, so the confirm names the
   * supplier and the amount rather than just asking "are you sure".
   */
  const remove = async (p) => {
    const who = p.supplier || p.supplier_code || `supplier ${p.supplier_id}`;
    const what = `${money(p.amount)} ${p.currency || ""}`.trim();

    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete the ${what} paid to ${who} on ${p.paid_at || "-"}?`)) return;

    setBusy(true);
    try {
      await axios.delete(`${API}/supplier-payments/${p.id}`);
      await fetchData();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e?.response?.data?.message || "Could not delete payment");
    }
    setBusy(false);
  };

  // filters are applied here rather than server-side, so they work the same
  // whichever of the two loaders ran
  const narrowed = rows.filter(
    (r) =>
      inDateRange(r.paid_at, from, to) && matches(r.supplier, supplier) && matches(r.method, method)
  );

  const filteredRows = filterRows(narrowed, query);
  const pager = usePagination(filteredRows);

  const total = filteredRows.reduce((s, r) => s + (Number(r.amount_base ?? r.amount) || 0), 0);

  const cell = { fontSize: 14, whiteSpace: "nowrap" };

  return (
    <DashboardLayout>
      <SuiBox py={1}>
        <Card>
          <FilterBar
            title="Supplier Payments"
            value={query}
            onChange={setQuery}
            count={filteredRows.length}
            total={rows.length}
            placeholder="Search by supplier, reference or invoice..."
            actions={
              <Button variant="contained" color="primary" onClick={() => setAdding(true)}>
                Record Payment
              </Button>
            }
          >
            <FilterSelect
              label="Supplier"
              icon="local_shipping"
              value={supplier}
              onChange={setSupplier}
              options={optionsFromRows(rows, "supplier")}
            />
            <FilterSelect
              label="Method"
              icon="account_balance"
              value={method}
              onChange={setMethod}
              options={optionsFromRows(rows, "method")}
            />
            <FilterDates label="Paid" from={from} to={to} onFrom={setFrom} onTo={setTo} />

            <SuiTypography variant="caption" sx={{ opacity: 0.75, whiteSpace: "nowrap" }}>
              {`Paid out ${money(total)}`}
            </SuiTypography>
          </FilterBar>

          {error && (
            <SuiBox px={3} pb={2}>
              <SuiTypography variant="button" color="error">
                {error}
              </SuiTypography>
            </SuiBox>
          )}

          {loading ? (
            <CargoLoader label="Loading payments" />
          ) : (
            <>
              <SuiBox sx={{ overflowX: "auto" }}>
                <Table sx={{ minWidth: 1150 }}>
                  <TableHead>
                    <TableRow>
                      <IdHead />
                      <TableCell align="center">Payment date</TableCell>
                      <TableCell align="center">Supplier</TableCell>
                      <TableCell align="center">Type</TableCell>
                      <TableCell align="center">Order</TableCell>
                      <TableCell align="center">Source</TableCell>
                      <TableCell align="center">Method</TableCell>
                      <TableCell align="center">Reference</TableCell>
                      <TableCell align="center">Amount</TableCell>
                      <TableCell align="center">Operations</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {pager.pageRows.map((p) => (
                      <TableRow key={p.id}>
                        <IdCell value={p.id} />
                        <TableCell align="center" sx={cell}>
                          {p.paid_at || "-"}
                        </TableCell>
                        <TableCell align="center" sx={{ ...cell, fontWeight: 700 }}>
                          {p.supplier_code && p.supplier_code !== p.supplier
                            ? `${p.supplier_code} - ${p.supplier}`
                            : p.supplier || p.supplier_code || "-"}
                        </TableCell>
                        <TableCell align="center" sx={cell}>
                          {p.type === "deposit" ? "Deposit" : "Payment"}
                        </TableCell>
                        <TableCell align="center" sx={cell}>
                          {p.shop_code || p.invoice_no || "-"}
                        </TableCell>
                        <TableCell align="center" sx={cell}>
                          {p.source || "-"}
                        </TableCell>
                        <TableCell align="center" sx={cell}>
                          {p.method || "-"}
                        </TableCell>
                        <TableCell align="center" sx={cell}>
                          {p.reference || "-"}
                        </TableCell>
                        <TableCell align="center" sx={{ ...cell, fontWeight: 700 }}>
                          {`${money(p.amount)} ${p.currency || ""}`}
                        </TableCell>

                        <TableCell align="center" sx={cell}>
                          <RowActions
                            actions={[
                              {
                                icon: "receipt_long",
                                label: "All payments for this supplier",
                                tone: "info",
                                onClick: () =>
                                  setHistory({
                                    id: p.supplier_id,
                                    name: p.supplier || p.supplier_code,
                                  }),
                              },
                              {
                                icon: "delete",
                                label: "Delete payment",
                                tone: "danger",
                                disabled: busy,
                                onClick: () => remove(p),
                              },
                            ]}
                          />
                        </TableCell>
                      </TableRow>
                    ))}

                    {filteredRows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                          <SuiTypography variant="button" color="text">
                            No payments
                          </SuiTypography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </SuiBox>
              <Pagination {...pager} />
            </>
          )}
        </Card>
      </SuiBox>

      <SupplierPaymentDialog
        open={Boolean(adding)}
        onClose={() => setAdding(null)}
        onSaved={fetchData}
      />

      {/* every payment made to one supplier, over the list */}
      <SupplierPaymentsDialog
        open={Boolean(history)}
        supplierId={history?.id}
        supplierName={history?.name}
        onClose={() => setHistory(null)}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default Payments;
